import React from 'react'
import MatchChart from 'lib/components/charts/match-chart'
import Container from 'lib/components/charts/container'
import XAxis from 'lib/components/charts/x-axis'
import YAxis from 'lib/components/charts/y-axis'
import Grid from 'lib/components/charts/grid'
import SmallLabelXAxis from 'lib/components/charts/small-label-x-axis'
import SparkLine from 'lib/components/charts/shapes/spark-line'
import Title from './title'
import { asDecimal, asFullYear } from 'lib/format'
import { scaleLog, scaleTime } from 'd3-scale'
import { extent } from 'd3-array'
import { getGutters } from 'lib/components/charts/layouts/gutters'
import LineChartTooltip from 'lib/components/charts/line-chart-tooltip'

class PerformanceHistory extends React.PureComponent {
  static defaultProps = {
    base: 2,
    data: [],
    width: 300,
    height: 225,
    formatX: asFullYear,
    formatY1: n => n,
    formatY2: n => n,
    labelX: '',
    labelY: ''
  }

  renderLine(x, y, { key, color, label, points }) {
    return (
      <SparkLine
        key={key}
        points={points}
        scaleX={x}
        scaleY={y}
        label={label}
        stroke={color}
      />
    )
  }

  getY1Scale() {
    const { base, datasets, domainY1 } = this.props

    const dataset = datasets[0]
    const y1 = scaleLog().base(base)

    if (domainY1) {
      y1.domain(domainY1)
    } else if (dataset) {
      y1.domain(extent(dataset.dataPoints, d => d.y1)).nice()
    } else {
      y1.domain([0, 5])
    }

    return y1
  }

  getY2Scale() {
    const { base, datasets, domainY2 } = this.props

    const dataset = datasets[1]
    const y2 = scaleLog().base(base)

    if (domainY2) {
      y2.domain(domainY2)
    } else if (dataset) {
      y2.domain(extent(dataset.dataPoints, d => d.y2)).nice()
    } else {
      y2.domain([0, 5])
    }

    return y2
  }

  getY2Ticks(scaleY1, scaleY2) {
    return scaleY1.ticks().map(y1Tick => scaleY2.invert(scaleY1(y1Tick))) // generate y2 ticks that line up with y1 ticks
  }

  getDomainX() {
    let values = this.props.datasets.reduce((memo, dataset) => {
      return memo.concat(dataset.points.map(point => point.x))
    }, [])

    return values.length ? extent(values) : [new Date(0), new Date()]
  }

  render() {
    const {
      datasets,
      labelX,
      labelY1,
      labelY2,
      formatX,
      formatY1,
      formatY2,
      width,
      height,
      primaryColor,
      title
    } = this.props

    const period = this.getDomainX()

    const x = scaleTime()
      .domain(period)
      .nice(8)

    const y1 = this.getY1Scale()
    const y2 = this.getY2Scale()
    const y2Ticks = this.getY2Ticks(y1, y2)

    let { left, top, bottom, right } = getGutters({
      width: width,
      height: height,
      left: {
        ticks: y1.ticks(),
        format: formatY1,
        label: labelY1.toUpperCase()
      },
      right: {
        ticks: y2.ticks(),
        format: formatY2,
        label: labelY2.toUpperCase()
      },
      bottom: {
        ticks: x.ticks(4),
        format: formatX,
        label: labelX.toUpperCase()
      }
    })

    x.range([left, width - right])

    y1.range([height - bottom, top])
    y2.range([height - bottom, top])

    const axisWidth = width - right

    return (
      <Container width={width} height={height}>
        <Grid scaleX={x} scaleY={y1} ticksX={x.ticks()} ticksY={y1.ticks()} />

        <Title x={left} y={top}>
          {title}
        </Title>

        {datasets.map((dataset, i) =>
          this.renderLine(x, i > 0 ? y2 : y1, dataset)
        )}

        <MatchChart breakpoint="large" width={width}>
          <XAxis
            label={labelX}
            scale={x}
            format={formatX}
            ticks={x.ticks()}
            height={bottom}
            y={y1.range()[0]}
          />
        </MatchChart>

        <MatchChart breakpoint="small" width={width}>
          <SmallLabelXAxis
            label={period.map(formatX).join(' - ')}
            x={left}
            y={height - bottom}
            width={axisWidth}
          />
        </MatchChart>

        <YAxis
          label={labelY1}
          scale={y1}
          format={formatY1}
          ticks={y1.ticks()}
          height={height}
          width={left}
          tickColor={primaryColor}
          labelColor={primaryColor}
        />

        <YAxis
          label={labelY2}
          scale={y2}
          format={formatY2}
          ticks={y2Ticks}
          height={height}
          width={right}
          x={x.range()[1]}
          inverted
        />

        {this.props.expanded && (
          <LineChartTooltip
            label="Performance History"
            datasets={datasets}
            formatY1={asDecimal(2)}
            formatY2={asDecimal(2)}
            labelY1={labelY1}
            labelY2={labelY2}
            width={width}
            height={height}
            left={left}
            top={top}
            bottom={bottom}
            right={right}
            scaleX={x}
            scaleY1={y1}
            scaleY2={y2}
          />
        )}
      </Container>
    )
  }
}

export default PerformanceHistory



// WEBPACK FOOTER //
// ./src/components/charts/performance-history.js